import { router } from "@inertiajs/react";
import { useEffect, useState } from "react";
import DomainPagePickerDialog, { majorityProbing } from "@/components/domains/DomainPagePickerDialog";
import type { DiscoverablePage } from "@/types";

type Props = {
  open: boolean;
  onClose: () => void;
  domainId: string;
  pages: DiscoverablePage[];
  discovering?: boolean;
  required?: boolean;
};

export default function EvaluatePagesDialog({
  open,
  onClose,
  domainId,
  pages,
  discovering = false,
  required = false,
}: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [probingById, setProbingById] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const canClose = !required;
  const needsSelection = selectedIds.size === 0;

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedIds(new Set());
    setProbingById(
      Object.fromEntries(
        pages.map((page) => [page.id, majorityProbing(pages.filter((entry) => entry.id !== page.id))]),
      ),
    );
  }, [open, pages]);

  const togglePage = (pageId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(pageId)) {
        next.delete(pageId);
      } else {
        next.add(pageId);
      }
      return next;
    });
  };

  const save = () => {
    if (needsSelection || submitting) {
      return;
    }

    const pageIds = Array.from(selectedIds);
    const pagesPayload = Object.fromEntries(
      pageIds.map((pageId) => [pageId, { full_application_probing: probingById[pageId] ?? false }]),
    );

    setSubmitting(true);
    router.post(
      `/domains/${domainId}/audits`,
      { page_ids: pageIds, pages: pagesPayload },
      {
        preserveScroll: true,
        onFinish: () => setSubmitting(false),
        onSuccess: onClose,
      },
    );
  };

  return (
    <DomainPagePickerDialog
      open={open}
      onClose={onClose}
      pages={pages}
      discovering={discovering}
      canClose={canClose}
      title={required ? "Choose pages to evaluate" : "Add pages to evaluate"}
      selectedSummary={`${selectedIds.size} ${selectedIds.size === 1 ? "page" : "pages"} selected to evaluate`}
      closeAriaLabel="Close evaluate pages dialog"
      checkboxAriaLabel={(page) => `Evaluate ${page.title || page.path}`}
      selectedIds={selectedIds}
      probingById={probingById}
      onTogglePage={togglePage}
      onProbingChange={(pageId, value) => setProbingById((current) => ({ ...current, [pageId]: value }))}
      needsSelection={needsSelection}
      hint={needsSelection ? "Select at least one page to start evaluating." : "Confirm to evaluate these pages now."}
      submitting={submitting}
      submitLabel={submitting ? "Starting…" : "Confirm"}
      onConfirm={save}
    />
  );
}
